const LOG_API_URL = `https://log.funfactory.com/log/`;
const IS_ITEM_LOGGED_KEY = Symbol('AAADataService.IS_ITEM_LOGGED');
const DEBOUNCE_TIME_IN_MILLISECONDS = 500;
const DEBOUNCE_TIME_MAX_WAIT_IN_MILLISECONDS = 2000;
const RETRY_DELAY_TIME_IN_MILLISECONDS = 5000;
const RETRY_DELAY_FACTOR = 2;

class AAADataService {
    #logDataDefault = null;
    #debounceTimeout = null;
    #debounceTimeoutMaxWait = null;
    #debounceDelay = DEBOUNCE_TIME_IN_MILLISECONDS;
    #isSending = false;
    #isSendingBeacon = false;
    #currentState = null;
    #currentHref = null;
    #requestId = null;
    #referrer = null;

    logPageData() {
        this.#clearTimeouts();
        const logData = this.#getDataToLog();

        if (!logData.dataLayer.length || this.#isSending) {
            return;
        }

        this.#isSending = true;
        this.#markDataLayerAsLogged(logData.dataLayer);

        this.#sendLogData(logData)
            .then(
                () => {
                    this.#debounceDelay = DEBOUNCE_TIME_IN_MILLISECONDS;
                },
                () => {
                    this.#markDataLayerAsLogged(logData.dataLayer, false);
                    this.#debounceDelay = Math.max(
                        RETRY_DELAY_TIME_IN_MILLISECONDS,
                        this.#debounceDelay * RETRY_DELAY_FACTOR
                    );
                }
            )
            .finally(() => {
                this.#isSending = false;
                if (this.#hasDataLayerValidItemsToLog()) {
                    this.#logPageDataDebounced();
                }
            });
    }

    init() {
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = [];
        const postParams = [];
        this.#requestId = window.crypto?.randomUUID() ?? Date.now();
        this.#currentState = history.state;
        this.#referrer = document.referrer || "Direct";
        this.#currentHref = location.href;

        urlParams.forEach((value, name) => {
            if (name.toLowerCase().startsWith("utm_")) {
                utmParams.push({ name, value });
            }
        });

        const searchterm = urlParams.get("fss") || "";
        if (searchterm != "") {
            postParams.push({ name: "searchterm", value: searchterm });
        }

        const event_type = "pageview";

        const stateAsJson = JSON.stringify(history.state);
        const currentReferrer = JSON.stringify(this.#currentState);

        if (currentReferrer !== stateAsJson) {
            this.#requestId = window.crypto?.randomUUID() ?? Date.now();
            this.#currentState = history.state;
            this.#referrer = this.#currentHref
        }

        this.#currentHref = location.href;


        this.#logDataDefault = {
            url: window.location.href,
            get_params: utmParams,
            referrer: this.#referrer,
            post_params: postParams,
            event_type: event_type,
            request_id: this.#requestId,
        };

        this.#listenForDataLayerChanges();
        this.#initSovendusListeners();
        this.#logPageDataDebounced();

        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                this.#logDataAsBeacon()
            }
        });

        window.addEventListener("pagehide", () => this.#logDataAsBeacon());
    }

    async #sendLogData(logData) {
        try {
            const response = await fetch(LOG_API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(logData),
            });

            if (!response.ok) {
                throw new Error("Failed to send log data.");
            }
        } catch (error) {
            console.error("Error occurred while sending log data:", error);

            throw error;
        }
    }

    #getDataLayerToLog() {
        return window.dataLayer.filter((item) => this.#isValidDataLayerItem(item));
    }

    #isValidDataLayerItem(item) {
        const isArray = Array.isArray(item);
        return !(item.isServerSideLogged || item[IS_ITEM_LOGGED_KEY])
        && ['[object Array]', '[object Object]'].includes(Object.prototype.toString.call(item))
        && (isArray ? item.length : Object.keys(item).length);
    }

    #getDataToLog() {
        return {
            ...this.#logDataDefault,
            dataLayer: this.#getDataLayerToLog(),
        };
    }

    #markDataLayerAsLogged(dataLayer, isLogged = true) {
        dataLayer.forEach((item) => (item[IS_ITEM_LOGGED_KEY] = isLogged));
    }

    #logDataAsBeacon() {
        if (!navigator.sendBeacon || this.#isSendingBeacon) return;
        const logData = this.#getDataToLog();
        if (!logData.dataLayer.length) return;
        this.#clearTimeouts();
        this.#markDataLayerAsLogged(logData.dataLayer);
        const data = new Blob([JSON.stringify(logData)], {
            type: "application/json",
        });
        navigator.sendBeacon(LOG_API_URL, data);
        this.#isSendingBeacon = true;
    }

    #logPageDataDebounced() {
        if (this.#isSending) return;
        clearTimeout(this.#debounceTimeout);
        this.#debounceTimeout = setTimeout(() => {
            this.logPageData();
        }, this.#debounceDelay);

        if (!this.#debounceTimeoutMaxWait) {
            this.#debounceTimeoutMaxWait = setTimeout(() => {
                this.logPageData();
            }, Math.max(DEBOUNCE_TIME_MAX_WAIT_IN_MILLISECONDS, this.#debounceDelay));
        }
    }

    #clearTimeouts() {
        clearTimeout(this.#debounceTimeout);
        clearTimeout(this.#debounceTimeoutMaxWait);
        this.#debounceTimeout = null;
        this.#debounceTimeoutMaxWait = null;
    }

    #onDataLayerChange() {
        this.#logPageDataDebounced();
    }

    #hasDataLayerValidItemsToLog(dataLayer = window.dataLayer) {
        return dataLayer.find((item) => this.#isValidDataLayerItem(item));
    }

    #listenForDataLayerChanges() {
        const oldPush = window.dataLayer.push;
        window.dataLayer.push = (...args) => {
            oldPush.apply(window.dataLayer, args);
            if (this.#hasDataLayerValidItemsToLog(Array.from(args))) {
                this.#onDataLayerChange(...args);
            }
        };
    }

    #initSovendusListeners() {
        window.addEventListener("message", (event) => {
            if (
                event.data?.channel === "sovendus-integration" &&
                event.data?.payload?.action === "openUrl" &&
                event.data?.payload?.url.includes("sovendus.com/entry/banner")
            ) {
                window.dataLayer?.push({ sovendus_click: true });
            }
        });
    }
}

new AAADataService().init();